<template>
    <div class="setting-content--section">
        <div v-if="!loading" :class="connectionContainerClass">
            <div v-if="connectionDetails.status !== 'completed'" class="index-column">
                <div class="index-number">1</div>
                <div class="vertical-line"></div>
            </div>

            <div v-if="connectionDetails.status !== 'completed'" class="content-container">
                <div v-show="!manualConnection">
                    <div class="step-title-container step-title-background">
                        <div class="step-title" v-translate>Connect to webshop</div>
                    </div>

                    <div class="step-content">
                        <b-form >
                            <b-form-group 
                                id="input-group-1" 
                                :label="$gettext('Fill out the URL of your webshop in Woocommerce and connect')" 
                                label-for="input-1">

                                    <div class="flex-row-display">
                                        <b-form-input
                                            id="input-1"
                                            v-model="woocomerceUrl"
                                            :placeholder="$gettext('https://www.my_online_webshop.com')"
                                            class="input-box"
                                            :state="woocomerceUrlState"
                                            @input="resetState('woocomerce_url')"
                                            required></b-form-input>
                                        <GekkoButton 
                                            @click="connect()" 
                                            :text="$gettext('Connect')" 
                                        />
                                    </div>
                                    
                            </b-form-group>
                        </b-form>
                        <div v-if="showUrlErrorMsg" class="url-feedback">{{getUrlFeedback(woocomerceUrl)}}</div>
                    </div>
                </div>
                
                <div v-show="manualConnection">
                    <div class="step-title-container step-title-background">
                        <div class="step-title" v-translate>Manual webshop connection</div>
                    </div>

                    <div class="step-content">
                        <span v-translate>Sometimes Gekko cannot automatically connect your webshop. This generally means your server setup is not as standard as we would like and we are not able to connect you in a simple way. But instead of forcing you to reset your servers, we also allow you to manually setup a connection to your Woocommerce webshop. You need to generate your Client Key and Secret for this.</span>
                        <h4 style="padding-top: 10px" v-translate>Instructions</h4>
                        <span v-translate>Please fill out the following fields and please remember the following instructions:</span>
                        <ul>
                            <li><translate>Use </translate><a href="https://docs.woocommerce.com/document/woocommerce-rest-api/#section-2" v-translate>this page</a><translate> to get more info on how to generate the Client Key and Client Secret</translate></li>
                            <li v-translate>Make sure you select in description: "Gekko - API Read/Write"</li>
                            <li v-translate>Make sure you select in permissions: "Read/Write"</li>
                        </ul>

                        <b-form>
                            <b-form-group 
                                id="input-group-2" 
                                :label="$gettext('Fill out the URL of your webshop')" 
                                label-for="input-2">
                                    <b-form-input
                                        id="input-2"
                                        v-model="woocomerceUrl"
                                        :placeholder="$gettext('https://www.my_online_webshop.com')"
                                        class="input-box"
                                        :state="woocomerceUrlState"
                                        @input="resetState('woocomerce_url')"
                                        required></b-form-input>
                                    <div v-if="showUrlErrorMsg" class="url-feedback">{{getUrlFeedback(woocomerceUrl)}}</div>
                            </b-form-group>

                            <b-form-group 
                                id="input-group-3" 
                                :label="$gettext('Client Key')" 
                                label-for="input-3">
                                    <b-form-input
                                        id="input-3"
                                        v-model="clientKey"
                                        placeholder="ck_ciuhuih78234fy89234y"
                                        class="input-box"
                                        :state="clientKeyState"
                                        @input="resetState('client_key')"
                                        required></b-form-input>
                            </b-form-group>

                            <b-form-group 
                                id="input-group-4" 
                                :label="$gettext('Client Secret')" 
                                label-for="input-4">
                                        <b-form-input
                                            id="input-4"
                                            v-model="clientSecret"
                                            placeholder="cs_8m238hqmfhafmbjbrf"
                                            class="input-box"
                                            :state="clientSecretState"
                                            @input="resetState('client_secret')"
                                            required></b-form-input>
                            </b-form-group>
                            <GekkoButton 
                                @click="manuallyConnect()" 
                                :text="$gettext('Connect')" 
                            />
                        </b-form>
                    </div>
                </div>
            </div>
            
            <div class="index-column">
                <div 
                    v-if="connectionDetails.status !== 'completed'" 
                    class="index-number" 
                    :class="[(connectionDetails.status === 'completed') ? 'text-blue' : 'text-unassigned']"
                >
                    2
                </div>
            </div>

            <div :class="contentContainerClass">
                <div class="step-title-container" :class="stepTitleBackgroundClass">
                    <div class="step-title" :class="[(connectionDetails.status === 'completed') ? 'text-black' : 'text-unassigned']" v-translate>
                        Gekko receives Woocommerce orders
                    </div>
                    <div class="action-items-container">
                        <div v-if="showActionButtonsDropdown" id="dropdown-container" class="dropdown-content-container">
                            <GekkoButton 
                                @click="reconnectWebhooks()"
                                :text="$gettext('Repair webhooks')"
                                icon="fa-solid fa-rotate-left"
                                variant="link"
                                class="text-info"
                            />
                            <GekkoButton 
                                @click="disconnect()"
                                :text="$gettext('Disconnect')"
                                icon="fa-solid fa-trash-can"
                                variant="link"
                                class="text-danger"
                            />
                        </div>

                        <span 
                            v-if="connectionDetails.status === 'completed'" 
                            @click="expandActionButtonsDropdown()" 
                            id="action-button-icon-wrap" 
                            class="action-button dropdown-icon"
                        >
                            <font-awesome-icon id="action-button-icon" icon="fa-solid fa-ellipsis-vertical" />
                        </span>


                        
                        <h4 v-if="connectionDetails.status === 'completed' && hooks_ok">
                            <b-badge 
                                variant="primary" 
                                class="action-button"
                                pill>
                                    <translate>Active</translate>
                            </b-badge>
                        </h4>    
                        <h4 v-if="connectionDetails.status === 'completed' && !hooks_ok" >
                            <b-badge
                                variant="danger" 
                                class="action-button" 
                                pill>
                                    <translate>Inactive</translate>
                            </b-badge>
                        </h4>
                    </div>
                </div>
                
                <div v-if="connectionDetails.status === 'completed'" class="step-content">
                    <h3 v-translate>Connection Details</h3>
                    <b-container>
                        <b-row class="info-row">
                            <b-col cols="3"><translate>Status:</translate></b-col>
                            <b-col cols="9">
                                <div v-if="connectionDetails.status === 'completed'" class="success-text" v-translate>Verified</div>
                                <div v-if="connectionDetails.status !== 'completed'" class="error-text" v-translate>In review</div>
                            </b-col>
                        </b-row>
                        <b-row v-if="connectionDetails.store_url" class="info-row">
                            <b-col cols="3"><translate>Webshop url:</translate></b-col>
                            <b-col cols="9"><a :href="connectionDetails.store_url">{{connectionDetails.store_url}}</a></b-col>
                        </b-row>
                        <b-row v-if="connectionDetails.created" class="info-row">
                            <b-col cols="3"><translate>Connection date:</translate></b-col>
                            <b-col cols="9"><div class="success-text">{{connectionDetails.created | formatDate}}</div></b-col>
                        </b-row>
                        <b-row class="info-row">
                            <b-col cols="3"><translate>Webhooks:</translate></b-col>
                            <b-col cols="9">
                                <div v-if="hooks_ok" class="success-text" v-translate>Connected</div>
                                <div v-if="!hooks_ok" class="error-text" style="color: #FF8C00" v-translate>Webhooks seem to be incorrectly set up. Please try to reconnect them.</div>
                            </b-col>
                        </b-row>
                        <b-row class="info-row">
                            <b-col cols="3"><translate>Created date:</translate></b-col>
                            <b-col cols="9">
                                <div v-if="created_hook" class="success-text">{{created_date | formatDate}}</div>
                                <div v-if="!created_hook" class="error-text" v-translate>Order created connection seems to be incorrect.</div>
                            </b-col>
                        </b-row>
                        <b-row class="info-row">
                            <b-col cols="3"><translate>Updated date:</translate></b-col>
                            <b-col cols="9">
                                <div v-if="updated_hook" class="success-text">{{updated_date | formatDate}}</div>
                                <div v-if="!updated_hook" class="error-text" v-translate>Order updated connection seems to be incorrect.</div>
                            </b-col>
                        </b-row>
                    </b-container>

                    <h3 style="padding-top: 10px" v-translate>Preferences</h3>
                    <b-form>
                         <b-form-group
                            id="input-group-5" 
                            :label="$gettext('Email invoices automatically to customers:')" 
                            label-for="input-5"
                            label-cols-sm="4"
                            class="mb-0"
                            v-slot="{ ariaDescribedby }">
                            <b-form-checkbox
                                    id="input-5"
                                    v-model="connectionDetails.send_invoice"
                                    class="input-box pt-2"
                                    :aria-describedby="ariaDescribedby"
                                    switch></b-form-checkbox>
                        </b-form-group>

                        <b-form-group 
                            id="input-group-6" 
                            :label="$gettext('Woocommerce email message:')" 
                            label-for="input-6">
                            <b-form-textarea
                                id="input-6"
                                v-model="connectionDetails.email_message"
                                :placeholder="$gettext('This is the email message when sending a Woocommerce invoice.')"
                                class="input-box"></b-form-textarea>
                        </b-form-group>

                        <b-form-group 
                            id="input-group-7" 
                            :label="$gettext('Woocommerce invoice message:')" 
                            label-for="input-7">
                            <b-form-textarea
                                id="input-7"
                                v-model="connectionDetails.invoice_message"
                                :placeholder="$gettext('This is the default message at the bottom of each Woocommerce invoice.')"
                                class="input-box"></b-form-textarea>
                        </b-form-group>

                        <GekkoButton 
                            @click="updatePreferences()" 
                            :text="$gettext('Update preferences')" 
                        />
                    </b-form>
                </div>
            </div>
            
            <div></div>
            <div v-if="connectionDetails.status !== 'completed'" @click="expandManualConnection" class="helping-text">{{helpText}}</div>
            <div v-if="connectionDetails.status === 'completed'" class="helping-text-note">
                <translate>Note: If the order hits "processing" or "completed"  status, Gekko converts it into an invoice which will be stored in your account.</translate>
            </div>
        </div>

        <div class="text-center">
            <b-spinner v-if="loading" style="width: 3rem; height: 3rem;" variant="primary" />
        </div>
    </div>
</template>

<script>
import WoocomerceService from "@/store/services/woocomerce.service";
import GekkoButton from "@/components/GekkoButton";

// mixins
import settingsPageSetup from "@/mixins/settingsPageSetup";

export default {
    name: "Webshop",
    components: { GekkoButton },
    mixins: [settingsPageSetup],
    data () {
        return {
            pageSettings: {
                title: this.$gettext("Webshop — WooCommerce"),
                showCompanyPicker: true
            },
            woocomerceUrl: "",
            clientKey: "",
            clientSecret: "",
            woocomerceUrlState: null,
            clientKeyState: null,
            clientSecretState: null,
            manualConnection: false,
            connectionDetails: {
                status: "not_started"
            },
            hooks_ok: false,
            created_date: null,
            updated_date: null,
            created_hook: false,
            updated_hook: false,
            showActionButtonsDropdown: false,
            showUrlErrorMsg: false,
            loading: true
        }
    },
    created() {
        this.getStatus();
        window.addEventListener("click", this.closeEventListener);
    },
    destroyed() {
        window.removeEventListener("click", this.closeEventListener);
    },
    methods: {
        closeEventListener(event) {
            if (event.target.id !== "action-button-icon" 
            && event.target.id !== "action-button-icon-wrap")
                this.showActionButtonsDropdown = false;
        },
        getStatus() {
            WoocomerceService.status({company_profile: window.company_id}).then((response) => {
                if (response.data.status !== "not_started") {
                    this.connectionDetails = response.data;
                    this.calculateHooks();
                } else {
                    this.connectionDetails.state = "not_started";
                }

                if (this.connectionDetails.status !== "not_started" && this.connectionDetails.status !== "completed")
                    this.manualConnection = true;
                
                this.loading = false;
            });
        },
        getUrlFeedback(url) {
            var expression = /^(http:\/\/|https:\/\/).*/;

            if (expression.test(url)) {
                expression = /^(http:\/\/www\.|https:\/\/www\.).*/;

                if (expression.test(url)) {
                    expression = /^(http:\/\/|https:\/\/).*?\/.*/;
                
                    if (expression.test(url)) {
                        return this.$gettext("Please do not include a path in the URL. ex: https://www.mydomain.com/path/");
                    } else {
                        return this.$gettext("The URL failed to validate. Please contact our team with the value you tried to enter for this field");
                    }
                } else {
                    expression = /^(http:\/\/|https:\/\/)[:.0-9]+$/;
                
                    if (expression.test(url)) {
                        return this.$gettext("If you are trying to connect an IP address, make sure the URL has the form: http://0.0.0.0/");
                    } else {
                        return this.$gettext("Please make sure to include the 'www.' in your url. ex: https://www.example.com/");
                    }
                }
            } else {
                return this.$gettext("The URL must begin with 'http://' or 'https://'");
            }
        },
        connect() {
            if (this.$store.getters['getIsPremium']) {
                if (WoocomerceService.isValidUrl(this.woocomerceUrl)) {
                    let data = {
                        webshop_url: this.woocomerceUrl,
                        company_profile: window.company_id
                    }
                    WoocomerceService.connect(data).then((response) => {
                        if (response.status === 200)
                            window.location.href = response.data;
                    }).catch(() => {
                        this.$store.commit("setAlert", {
                            title: this.$gettext("Oops!"),
                            text: this.$gettext("Something went wrong."),
                            variant: "danger",
                            duration: 4
                        });
                    });
                } else {
                    this.$store.commit("setAlert", {
                        title: this.$gettext("Error"),
                        text: this.$gettext("Please enter a valid URL."),
                        variant: "danger",
                        duration: 4
                    });
                    this.woocomerceUrlState = false;
                    this.showUrlErrorMsg = true;
                }
            } else {
                this.$store.commit('togglePremiumModal', 'web_invoices');
            }
        },
        manuallyConnect() {
            if (this.$store.getters['getIsPremium']) {
                if (!WoocomerceService.isValidUrl(this.woocomerceUrl)) {
                    this.$store.commit("setAlert", {
                        title: this.$gettext("Error"),
                        text: this.$gettext("Please enter a valid URL."),
                        variant: "danger",
                        duration: 4
                    });
                    this.woocomerceUrlState = false;
                    this.showUrlErrorMsg = true;
                } else if (!this.clientKey.length) {
                    this.clientKeyState = false;
                } else if (!this.clientSecret.length) {
                    this.clientSecretState = false;
                } else {
                    let data = {
                        "webshop_url": this.woocomerceUrl,
                        "client_key": this.clientKey,
                        "client_secret": this.clientSecret,
                        "company_profile": window.company_id
                    }

                    WoocomerceService.manuallyConnect(data).then(function (response) {
                        if (response.status === 200)
                            window.location.href = response.data;
                    }).catch(() => {
                        this.$store.commit("setAlert", {
                            title: this.$gettext("Oops!"),
                            text: this.$gettext("Something went wrong."),
                            variant: "danger",
                            duration: 4
                        });
                    });
                }
            } else {
                this.$store.commit('togglePremiumModal', 'web_invoices');
            }
        },
        updatePreferences() {
            let data = {
                company_profile: window.company_id,
                email_message: this.connectionDetails.email_message,
                invoice_message: this.connectionDetails.invoice_message,
                send_invoice: this.connectionDetails.send_invoice
            }

            WoocomerceService.updateSettings(data).then((response) => {
                if (response.status === 200) {
                    this.$store.commit("setAlert", {
                        title: this.$gettext("Success!"),
                        text: this.$gettext("Preferences updated."),
                        variant: "success",
                        duration: 4
                    });
                }
            }).catch(() => {
                this.$store.commit("setAlert", {
                    title: this.$gettext("Oops!"),
                    text: this.$gettext("Something went wrong."),
                    variant: "danger",
                    duration: 4
                });
            });
        },
        disconnect() {
            if (confirm(this.$gettext("Are you sure you want to disconnect from Woocommerce?"))) {
                this.loading = true;
                WoocomerceService.disconnect({company_profile: window.company_id}).then(() => {
                    window.location.reload();
                }).catch(() => {
                    this.$store.commit("setAlert", {
                        title: this.$gettext("Oops!"),
                        text: this.$gettext("Something went wrong."),
                        variant: "danger",
                        duration: 4
                    });
                });
            }
        },
        reconnectWebhooks() {
            this.loading = true;
            WoocomerceService.reconnectWebhooks({company_profile: window.company_id}).then(() => {
                this.getStatus();
            }).catch(() => {
                this.$store.commit("setAlert", {
                    title: this.$gettext("Oops!"),
                    text: this.$gettext("Something went wrong."),
                    variant: "danger",
                    duration: 4
                });
            });
        },
        calculateHooks() {
            for(var hook_id in this.connectionDetails.webhook_list) {
                var hook = this.connectionDetails.webhook_list[hook_id];
                hook.date_created = new Date(hook.date_created);
                
                if(hook.topic == 'order.created'){
                    this.created_hook = true;
                    this.created_date = hook.date_created;
                }if(hook.topic == 'order.updated') {
                    this.updated_hook = true;
                    this.updated_date = hook.date_created;
                }
            }

            if(this.created_hook && this.updated_hook && this.connectionDetails.webhook_list.length==2)
                this.hooks_ok = true;
        },
        expandManualConnection() {
            this.manualConnection = !this.manualConnection;
        },
        resetState(parameter) {
            if (parameter === "woocomerce_url")
                this.woocomerceUrlState = null;
            else if (parameter === "client_key")
                this.clientKeyState = null;
            else if (parameter === "client_secret")
                this.clientSecretState = null;
            this.showUrlErrorMsg = false;
        },
        expandActionButtonsDropdown() {
            this.showActionButtonsDropdown = !this.showActionButtonsDropdown;
        }
    },
    computed: {
        helpText() {
            if (!this.manualConnection)
                return this.$gettext('Does automatic connection not work?');
            else
                return this.$gettext('Back to automatic connection');
        },
        connectionContainerClass() {
            if (this.connectionDetails.status === "completed")
                return "connection-block-container";
            else 
                return "connection-grid-container";
        },
        stepTitleBackgroundClass() {
            if (this.connectionDetails.status !== "completed")
                return "";
            else
                return "step-title-background";
        },
        contentContainerClass() {
            if (this.connectionDetails.status !== "completed")
                return "";
            else
                return "content-container";
        }
    }
}
</script>

<style lang="scss" scoped>
$bulletSize: 50px;

.connection-grid-container {
    display: grid;
    grid-template-columns: $bulletSize auto;
}

.connection-block-container {
    display: block;
}

.flex-row-display {
    display: flex;
    flex-direction: row;
}

.index-column {
    width: $bulletSize;
}

.content-container {
    border: solid $colorBorderGrey 1px;
    border-radius: 10px;
    margin-bottom: 20px;
    margin-left: 10px;
}

.index-number {
    text-align: center;
    padding: 2px 0;
    font-size: 30px;
    font-weight: 900;
    color: $colorBlue;
    border: solid 2px $colorTextSemi;
    border-radius: 50%;
    height: $bulletSize;
}

.vertical-line {
    width: 50%;
    height: calc(100% - #{$bulletSize});
    border-right: 1px solid $colorBorderGrey;
}

.step-title-container {
    display: flex;
    flex-direction: row;
    align-items: center;
}

.step-title-background {
    border-bottom: solid $colorBorderGrey 1px;
    background-color: $colorBrand50;
    border-radius: 10px 10px 0px 0px;
}

.step-title {
    padding-left: 15px;
    font-size: 30px;
    font-weight: 900;
    width: 100%;
}

.input-box {
    margin-right: 10px;
    max-width: 500px;
}

.step-content {
    padding-left: 15px;
    padding-bottom: 10px;
    padding-top: 10px;
}

.action-items-container {
    width: 100%;
}

.dropdown-icon {
    color: $colorBorderGrey;
    padding: 2px;
}

.dropdown-icon:hover {
    color: $colorTextSemi;
    cursor: pointer;
}

.dropdown-content-container {
    position: absolute;
    right: 20px;
    margin-top: 40px;
    font-size: 14px;
    padding: calc(15px / 2);
    border-radius: 12px;
    border-color: $colorBorderDash;
    box-shadow: -5px 6px 6px #0000005c;
    background: #ffffff;
    z-index: 10;
}

.url-feedback {
    color: $colorDanger;
}

.success-text {
    color: $colorTextSemi;
}

.error-text {
    font-style: italic;
    color: $colorTextSemi;
}

.info-row {
    padding-top: 3px;
    padding-bottom: 3px;
}

.helping-text {
    color: $colorBlue;
    font-size: 14px;
    margin-top: 5px;
}

.helping-text:hover {
    cursor: pointer;
    text-decoration: underline;
    color: $colorBlue800;
}

.helping-text-note {
    color: $colorBlue;
    font-size: 14px;
    margin-top: 20px;
}

.action-button {
    float: right;
    margin-right: 10px;
}
</style>